import React, { useEffect, useState } from 'react';
import t from 'utils/translate';
import { useSelector } from 'react-redux';
import SimpleModal from 'modules/shared/components/simple-modal';
import Button from '@mui/material/Button';
import LabelWithSwitcher from 'modules/shared/components/label-with-switcher';
import SelectInput from 'modules/shared/components/select-input';
import { MenuItem } from '@mui/material';
import { registerSearchInput, unregisterSearchInput } from 'modules/contacts/stores/contacts-slice-handler';
import ContactSearchInput from 'modules/contacts/components/contact-search-input';
import PhoneNumberInput from 'modules/shared/components/phone-number-input';
import { matchIsValidTel } from 'mui-tel-input';
import { addPhoneNumberPlus } from 'utils/functions';
import {
    saveCallFwdSettings,
    selectCallFwdDialogOpen,
    toggleCallFwdDialogOpen,
} from '../stores/user-settings-slice-handler';
import {
    selectCompanySettings,
    selectDataLoaded,
    callFwdSettingsAreEqual,
} from '../stores/company-settings-slice-handler';
import Preloader from '../../shared/components/preloader';

function CallForwardingDialog() {
    const companySettingsLoaded = useSelector(selectDataLoaded);

    if (!companySettingsLoaded) {
        return '';
    }

    /**
     * @type {CallForwardingReason[]}
     */
    const reasons = ['busy', 'unanswered', 'unreachable', 'unconditional'];

    const [contactSearchInputUuids, setContactSearchInputUuids] = useState({});
    const [dataIsSaving, setDataIsSaving] = useState(false);

    useEffect(() => {
        const uuids = {};
        reasons.forEach((reason) => {
            uuids[reason] = registerSearchInput();
        });

        setContactSearchInputUuids(uuids);

        setDataIsSaving(false);

        return () => Object.keys(contactSearchInputUuids).forEach((reason) => unregisterSearchInput(uuids[reason]));
    }, []);

    /**
     * @param {CallForwardingProfileData[]} settings
     */
    const settingsAreValid = (settings) => {
        let result = true;

        settings.forEach((setting) => {
            if (!result) {
                return;
            }

            if (setting.status === 'disabled') {
                return;
            }

            /**
             * @type {CallForwardingDestinationType[]}
             */
            const withNumber = ['internal', 'external'];

            if (
                withNumber.includes(setting.type) &&
                (!setting.number || !matchIsValidTel(addPhoneNumberPlus(setting.number)))
            ) {
                result = false;
            }
        });

        return result;
    };

    const dialogOpen = useSelector(selectCallFwdDialogOpen);
    /**
     * @type {CompanySettings}
     */
    const companySettings = useSelector(selectCompanySettings);
    let internalStateSettings = companySettings.CallForwardingSettings;
    const defaultFwdSettings = companySettings.CallForwardingDefaultProfileSettings;

    // when the user has the default call FWD profile assigned, and cannot make changes to those settings
    // the default settings must be shown instead its current ones which are empty
    if (companySettings.CallForwardingType === 0 && defaultFwdSettings !== null && !canEditSettings) {
        internalStateSettings = defaultFwdSettings;
    }

    const [currentSettings, setCurrentSettings] = useState(internalStateSettings);

    const canEditSettings = companySettings.CanChangeCallForwardingSettings;
    const saveButtonEnabled =
        !callFwdSettingsAreEqual(currentSettings, companySettings.CallForwardingSettings) &&
        settingsAreValid(currentSettings);
    const defaultButtonVisible =
        canEditSettings &&
        defaultFwdSettings !== null &&
        !callFwdSettingsAreEqual(currentSettings, defaultFwdSettings) &&
        !dataIsSaving;

    // update the dialog when was changed from outside
    useEffect(() => {
        setCurrentSettings(companySettings.CallForwardingSettings);
    }, [companySettings]);

    /**
     * Close the dialog.
     * @param {boolean} reset Reset any change.
     */
    const closeDialog = (reset = true) => {
        if (reset) {
            setCurrentSettings(companySettings.CallForwardingSettings);
        }

        toggleCallFwdDialogOpen(false);
    };

    /**
     * @param {CallForwardingReason} reason
     * @param {boolean} val
     */
    const toggleSetting = (reason, val) => {
        const newCurrentSettings = [...currentSettings];

        newCurrentSettings.forEach((setting, index) => {
            if (setting.reason !== reason) {
                // ALWAYS enabled => everything else is disabled
                if (reason === 'unconditional' && val) {
                    newCurrentSettings[index] = { ...newCurrentSettings[index], status: 'disabled' };
                }
            } else {
                newCurrentSettings[index] = { ...newCurrentSettings[index], status: val ? 'enabled' : 'disabled' };
            }
        });

        setCurrentSettings(newCurrentSettings);
    };

    /**
     *
     * @param {CallForwardingReason} reason
     * @param {CallForwardingDestinationType|null} settingType
     * @param {string|null} number
     */
    const updateSetting = (reason, settingType = null, number = null) => {
        const newCurrentSettings = [...currentSettings];

        newCurrentSettings.forEach((setting, index) => {
            if (setting.reason !== reason) {
                return;
            }

            const update = {};

            if (settingType !== null) {
                update.type = settingType;
            }

            if (number !== null) {
                update.number = number;
            }

            newCurrentSettings[index] = { ...newCurrentSettings[index], ...update };
        });

        setCurrentSettings(newCurrentSettings);
    };

    const applyDefaults = () => {
        setCurrentSettings(defaultFwdSettings);
    };

    const saveData = () => {
        setDataIsSaving(true);

        saveCallFwdSettings(currentSettings).then(() => {
            setDataIsSaving(false);
        });
    };

    /**
     * Render a call FWD setting.
     * @param {CallForwardingReason} reason
     */
    const renderSetting = (reason) => {
        const setting = currentSettings.filter((data) => data.reason === reason)[0];
        let title;
        let info;

        switch (reason) {
            case 'busy':
                title = t('settings_call_forward_when_busy');
                info = t('settings_call_forward_when_busy_tooltip');
                break;

            case 'unanswered':
                title = t('settings_call_forward_when_unanswered');
                info = t('settings_call_forward_when_unanswered_tooltip');
                break;

            // the ALWAYS option
            case 'unconditional':
                title = t('settings_call_forward_always');
                info = t('settings_call_forward_always_tooltip');
                break;

            case 'unreachable':
                title = t('settings_call_forward_when_unreachable');
                info = t('settings_call_forward_when_unreachable_tooltip');
                break;

            // no default
        }

        const settingDisabled =
            !canEditSettings ||
            (reason !== 'unconditional' &&
                currentSettings.filter((s) => s.reason === 'unconditional')[0].status === 'enabled');

        return (
            <div className="call-fwd-setting" key={reason} data-test-id={`call-fwd-setting ${reason}`}>
                <div className="setting-name">
                    <LabelWithSwitcher
                        labelContent={title}
                        tooltipContent={info}
                        onChange={(e) => toggleSetting(reason, e.target.checked)}
                        checked={setting.status === 'enabled'}
                        disabled={settingDisabled}
                    />
                </div>
                <div className="setting-value">
                    {setting.status === 'enabled' && (
                        <>
                            <div className="selector-container">
                                <SelectInput
                                    value={setting.type}
                                    disabled={settingDisabled}
                                    onChange={(e) => updateSetting(reason, e.target.value)}
                                >
                                    {companySettings.AllowVoicemail && (
                                        <MenuItem value="voicemail">{t('js_all_voicemail')}</MenuItem>
                                    )}
                                    <MenuItem value="internal">{t('settings_call_forward_contacts')}</MenuItem>
                                    {companySettings.AllowCallOut && (
                                        <MenuItem value="external">
                                            {t('settings_call_forward_external_number')}
                                        </MenuItem>
                                    )}
                                </SelectInput>
                            </div>
                            <div className="input-container">
                                {setting.type === 'internal' && (
                                    <ContactSearchInput
                                        uuid={contactSearchInputUuids[reason]}
                                        contactType="subscribers"
                                        renderContacts
                                        onlyHumans
                                        mandatory
                                        disabled={settingDisabled}
                                        selectedContactId={setting.number}
                                        onContactSelected={(user) => {
                                            updateSetting(reason, null, user.Msisdn);
                                        }}
                                        onContactReset={() => {
                                            updateSetting(reason, null, '');
                                        }}
                                    />
                                )}
                                {setting.type === 'external' && (
                                    <div className="external-number-container">
                                        <PhoneNumberInput
                                            value={setting.number}
                                            mandatory
                                            disabled={settingDisabled}
                                            onChange={(phone) => {
                                                updateSetting(reason, null, phone);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    // just cut it short
    if (!dialogOpen) {
        return '';
    }

    return (
        <SimpleModal
            className="call-fwd-dialog"
            dialogTitle={t('settings_call_forward')}
            closeCallback={() => closeDialog()}
            buttons={
                <div className="call-fwd-buttons-container">
                    <Button variant="contained" role="cancel" onClick={() => closeDialog()}>
                        {t('js_all_cancel')}
                    </Button>
                    <div>
                        {defaultButtonVisible && (
                            <Button variant="contained" role="primary" onClick={() => applyDefaults()}>
                                {t('settings_default')}
                            </Button>
                        )}
                        {canEditSettings && (
                            <Button
                                variant="contained"
                                role="primary"
                                disabled={!saveButtonEnabled}
                                onClick={() => saveData()}
                            >
                                {t('js_all_save')}
                            </Button>
                        )}
                    </div>
                </div>
            }
        >
            {dataIsSaving && <Preloader asOverlay />}
            <div className="call-fwd-settings-container">
                {renderSetting('busy')}
                {renderSetting('unanswered')}
                {renderSetting('unreachable')}
                {renderSetting('unconditional')}
            </div>
        </SimpleModal>
    );
}
export default CallForwardingDialog;
